import React, { useEffect, useState } from "react";
import { ArrowLeft, Send, Archive, RotateCcw, UserRound } from "lucide-react";
import { toast } from "sonner";
import { parApi, parErr } from "@/lib/partnerApi";
import { fmtDateTime } from "@/lib/residentApi";
import { inputCls, GoldButton, Pill } from "@/components/nds/windows/resident/ui";
import { AttachButton, FileChips } from "./MemoFiles";
import { whoLine } from "./MemoList";
import { MemoAssist } from "@/components/memo/MemoAssist";

const Entry = ({ e, testid }) => (
  <div data-testid={testid} className={`pl-3 border-l-2 space-y-1.5 ${e.by_kind === "partner" || e.author?.kind === "partner" ? "border-sky-400/50" : "border-nex-gold/50"}`}>
    <div className="text-[11px] text-nex-muted">{e.author?.name} · {e.author?.kind === "partner" ? "partner" : "TEA staff"} · {fmtDateTime(e.at || e.created_at)}</div>
    <p className="text-sm text-nex-silver/90 whitespace-pre-wrap">{e.body}</p>
    <FileChips files={e.attachments} testid={`${testid}-files`} />
  </div>
);

const Reply = ({ m, onSaved }) => {
  const [body, setBody] = useState("");
  const [files, setFiles] = useState([]);
  const [busy, setBusy] = useState(false);
  const send = async () => {
    setBusy(true);
    try { onSaved(await parApi.post(`/memos/${m.id}/replies`, { body, attachment_ids: files.map((x) => x.id) })); setBody(""); setFiles([]); }
    catch (e) { toast.error(parErr(e)); } finally { setBusy(false); }
  };
  return (
    <div className="space-y-2 pt-2 border-t border-white/5" data-testid="pmemo-reply-form">
      <textarea rows={3} value={body} onChange={(e) => setBody(e.target.value)} placeholder="Write a reply…" data-testid="pmemo-reply-input" className={inputCls} />
      <MemoAssist value={body} onChange={setBody} id="pmemo-reply" />
      <FileChips files={files} onRemove={(id) => setFiles(files.filter((x) => x.id !== id))} testid="pmemo-reply-files" />
      <div className="flex items-center gap-3">
        <AttachButton onAdd={(x) => setFiles((fs) => [...fs, x])} testid="pmemo-reply-attach" />
        <GoldButton onClick={send} disabled={!body.trim() || busy} data-testid="pmemo-reply-send" className="ml-auto"><Send className="h-3 w-3" /> Reply</GoldButton>
      </div>
    </div>
  );
};

const CloseBar = ({ m, side, onSaved }) => {
  const [open, setOpen] = useState(false);
  const [reason, setReason] = useState("");
  const act = (p) => p.then(onSaved).catch((e) => toast.error(parErr(e)));
  if (m.status !== "open") return side === "staff" ? (
    <button type="button" onClick={() => act(parApi.patch(`/memos/${m.id}/reopen`))} data-testid="pmemo-reopen" className="inline-flex items-center gap-1 font-mono text-[10px] uppercase text-nex-gold"><RotateCcw className="h-3 w-3" /> Reopen</button>
  ) : null;
  return open ? (
    <div className="flex flex-wrap gap-2" data-testid="pmemo-close-form">
      <input value={reason} onChange={(e) => setReason(e.target.value)} placeholder="Why it's closed (optional)" data-testid="pmemo-close-reason" className={`${inputCls} !py-1 flex-1`} />
      <button type="button" onClick={() => act(parApi.patch(`/memos/${m.id}/close`, { reason }))} data-testid="pmemo-close-confirm" className="font-mono text-[10px] uppercase text-nex-gold">Close it</button>
      <button type="button" onClick={() => setOpen(false)} data-testid="pmemo-close-cancel" className="font-mono text-[10px] uppercase text-nex-muted">Cancel</button>
    </div>
  ) : <button type="button" onClick={() => setOpen(true)} data-testid="pmemo-close" className="inline-flex items-center gap-1 font-mono text-[10px] uppercase text-nex-muted hover:text-nex-silver"><Archive className="h-3 w-3" /> Close memo</button>;
};

export default function MemoThread({ memo, side, onBack, onChanged }) {
  const [m, setM] = useState(memo);
  useEffect(() => { setM(memo); }, [memo]);
  const unread = memo.unread;
  useEffect(() => { if (unread) parApi.post(`/memos/${memo.id}/read`).then(onChanged).catch(() => {}); }, [memo.id, unread]); // eslint-disable-line react-hooks/exhaustive-deps
  const saved = (x) => { if (x?.id) setM(x); onChanged(); };
  return (
    <div data-testid="pmemo-thread" className="space-y-4">
      <button type="button" onClick={onBack} data-testid="pmemo-thread-back" className="inline-flex items-center gap-1 font-mono text-[10px] tracking-[0.22em] uppercase text-nex-gold"><ArrowLeft className="h-3.5 w-3.5" /> Memos</button>
      <div className="space-y-1.5">
        <div className="flex flex-wrap items-center gap-2">
          <h3 data-testid="pmemo-thread-subject" className="text-lg font-semibold text-nex-silver">{m.subject}</h3>
          {m.priority === "urgent" && <Pill tone="red" data-testid="pmemo-thread-urgent">Urgent</Pill>}
          <Pill tone={m.status === "open" ? "gold" : "dim"} data-testid="pmemo-thread-status">{m.status}</Pill>
        </div>
        <div className="flex flex-wrap items-center gap-3 text-[11px] text-nex-muted">
          <span data-testid="pmemo-thread-who">{whoLine(m, side)}</span>
          {m.resident && <span data-testid="pmemo-thread-resident" className="inline-flex items-center gap-1 text-sky-300"><UserRound className="h-3 w-3" /> {m.resident.name}</span>}
          {m.close_reason && <span>Closed: {m.close_reason}</span>}
        </div>
      </div>
      <Entry e={m} testid="pmemo-entry-first" />
      {(m.replies || []).map((r) => <Entry key={r.id} e={r} testid={`pmemo-entry-${r.id}`} />)}
      {m.status === "open" && <Reply m={m} onSaved={saved} />}
      <CloseBar m={m} side={side} onSaved={saved} />
    </div>
  );
}
